import { apiFetch } from './client';

// #323 — customer records: the company itself, as opposed to the credentials it
// authenticates with (intakeClients.ts for machines, customerUsers.ts for
// people). bcid-admin only; the API 403s every other role.
//
// This is the namespace authority. Creating either kind of credential 422s
// unless an ACTIVE customer already exists here, which is what makes a
// portal-only customer — one with logins and no machine client — legitimate.
//
// There is no delete. A company with history is suspended, never removed —
// every submission, case and credential is filed under its customer_id, which
// is the RLS key, so removing the row would orphan all of it. NOT the same
// reasoning as a portal login, which since #463 CAN be deleted: that one has a
// tombstone to keep its audit name alive, and a company has no equivalent.

/** A requester contact: who at this company may ask for a new portal login. */
export interface RequesterContact {
  name?: string;
  email?: string;
  phone?: string;
  [key: string]: unknown;
}

export type CustomerStatus = 'ACTIVE' | 'SUSPENDED';

export interface Customer {
  /** The tenant key. Immutable — it is the RLS key on every customer-scoped row. */
  customerId: string;
  name: string;
  status: CustomerStatus;
  requesterContacts: RequesterContact[];
  /** #867 — a reseller may have subaccounts (one level deep). */
  isReseller: boolean;
  /** #867 — the reseller this customer is a subaccount of, or null. */
  parentCustomerId: string | null;
  /**
   * #880 — who imposed a SUSPENDED company's suspension: its reseller (a
   * subaccount suspended from the customer portal) or 382. Null while ACTIVE.
   * Served by the two GETs only, hence optional. A reseller can lift its own
   * suspension and never 382's; suspending again from here takes a reseller's
   * suspension over as 382's.
   */
  suspendedBy?: 'reseller' | '382' | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What a suspension did to the company's Keycloak credentials.
 *
 * Best-effort by design: the DB status is what refuses the company's requests,
 * so the API commits it first and disables credentials afterwards. That means a
 * partial failure is possible and the operator has to see it — hence one item
 * per credential with its own outcome rather than a single boolean.
 */
export interface CleanupItem {
  kind: 'intake-client' | 'portal-login';
  id: string;
  outcome: 'disabled' | 'already-disabled' | 'failed';
  detail?: string;
}

export interface CredentialCleanup {
  /** false on reactivation — nothing is re-enabled, deliberately. */
  attempted: boolean;
  items: CleanupItem[];
  note: string;
}

export interface CustomerStatusResult extends Customer {
  credentialCleanup: CredentialCleanup;
}

export async function listCustomers(): Promise<Customer[]> {
  const res = await apiFetch<{ items: Customer[] }>('/admin/customers');
  return res.items;
}

export async function getCustomer(customerId: string): Promise<Customer> {
  return apiFetch<Customer>(`/admin/customers/${encodeURIComponent(customerId)}`);
}

export interface CreateCustomerInput {
  customerId: string;
  name: string;
  requesterContacts?: RequesterContact[];
}

export async function createCustomer(input: CreateCustomerInput): Promise<Customer> {
  return apiFetch<Customer>('/admin/customers', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Editable fields only. `customerId` is absent on purpose — the API rejects it
 * with a 400 (`additionalProperties: false` plus an explicit check), because
 * renaming the tenant key would orphan every row that references it.
 */
export interface UpdateCustomerInput {
  name?: string;
  requesterContacts?: RequesterContact[];
}

export async function updateCustomer(customerId: string, patch: UpdateCustomerInput): Promise<Customer> {
  return apiFetch<Customer>(`/admin/customers/${encodeURIComponent(customerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function setCustomerStatus(
  customerId: string,
  status: CustomerStatus,
): Promise<CustomerStatusResult> {
  return apiFetch<CustomerStatusResult>(`/admin/customers/${encodeURIComponent(customerId)}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

/**
 * #867 — flag or un-flag a reseller. Un-flagging one that still has
 * subaccounts is refused with 409 by the database's one-level rule; the
 * problem detail names the rule, so callers surface it as-is.
 */
export async function setCustomerReseller(customerId: string, isReseller: boolean): Promise<Customer> {
  return apiFetch<Customer>(`/admin/customers/${encodeURIComponent(customerId)}/reseller`, {
    method: 'PUT',
    body: JSON.stringify({ isReseller }),
  });
}

/**
 * #867 — attach a customer to a reseller as a subaccount, or detach it (null).
 * The parent must be a reseller that is not itself a subaccount (409 otherwise);
 * a self-parent is 400 and an unknown parent 422.
 */
export async function setCustomerParent(customerId: string, parentCustomerId: string | null): Promise<Customer> {
  return apiFetch<Customer>(`/admin/customers/${encodeURIComponent(customerId)}/parent`, {
    method: 'PUT',
    body: JSON.stringify({ parentCustomerId }),
  });
}

/**
 * The tenant-key shape for a new company, mirrored from the API's create schema
 * for affordance only. Same grammar as an intake client's name, so the client id
 * is always `bcid-intake-<customerId>` with nothing to choose — see the note on
 * CUSTOMER_ID_PATTERN in apps/api/src/routes/admin-customers.ts. Companies that
 * predate this rule can be looser; this governs creation only.
 */
export const CUSTOMER_ID_RE = /^[a-z0-9][a-z0-9-]{1,30}$/;

// #514/#516 — the staff-readable directory: id, name and status, plus the
// reseller shape since #867.
//
// Distinct from listCustomers() above, which reads the admin-only surface and
// carries requester contacts with it. This one is available to analyst and
// approver as well, which is what lets the Cases list and the case header show
// a company NAME rather than a slug for the people who actually work cases.
export interface StaffCustomer {
  customerId: string;
  name: string;
  status: CustomerStatus;
  isReseller: boolean;
  parentCustomerId: string | null;
}

export async function listStaffCustomers(): Promise<StaffCustomer[]> {
  const res = await apiFetch<{ items: StaffCustomer[] }>('/customers');
  return res.items;
}
